/** A replayed run read for its screens: how the real ticket's recording answered each call, and when it ran. */

import type { BulkRunToolCall, RunRecording } from '@/services/ai-function.service';

/**
 * Why a replay answered one of its mocked calls as it did, as Brahma said for that call; undefined for a
 * call no replay answered, such as an engine step like fill_template, which never calls a tool.
 */
export const replayWhy = (call: Pick<BulkRunToolCall, 'recorded' | 'grounded'>): string | undefined => {
	if (call.recorded == null) return undefined;
	if (call.recorded) return 'No tool was called. This is the answer the real ticket got.';
	return call.grounded
		? "Not in the recording, so answered from the scenario world, bent to agree with the real ticket's answers."
		: 'Not in the recording, so answered from the scenario world.';
};

/** When the real turn ran, as the run says it: "Oct 20, 2026, 10:02 AM". */
const recordedWhen = (iso: string | null): string | null => {
	const date = iso ? new Date(iso) : null;
	return date && !Number.isNaN(date.getTime())
		? date.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
		: null;
};

/** A replayed run's line beside its tool calls, and what its tooltip adds about the time and the protocol. */
export const replayLine = (recording: RunRecording): { label: string; title: string | undefined } => {
	const when = recordedWhen(recording.recordedAt);
	const report = recording.report;
	const notes = report
		? [
				report.recordedTimeUsed && when ? 'The AI was told this time as now.' : null,
				report.workflowsHeld.length ? null : 'Not held to this protocol, so the AI could pick any protocol.'
			]
		: ['The recording was sent, but the AI did not report what it used from it.'];
	return {
		label: `replayed from the real ticket${when ? `, ${when}` : ''}`,
		title: notes.filter(Boolean).join(' ') || undefined
	};
};
